import React from 'react';

import { cssMap } from '@compiled/react';

import Checkbox from '@atlaskit/checkbox';
import { TimePicker } from '@atlaskit/datetime-picker';
import { Label, RequiredAsterisk } from '@atlaskit/form';
import { Box, Stack } from '@atlaskit/primitives/compiled';
import Select, {
	components as selectParts,
	type OptionProps,
	type OptionType,
} from '@atlaskit/select';
import TextArea from '@atlaskit/textarea';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import {
	SCHEDULED_CHAT_DAYS,
	type ScheduledChatDayOfWeek,
	type ScheduledChatFrequency,
} from '@atlassian/conversation-assistant-message-actions-api/scheduled-chat';

import { FREQUENCY_VALUES } from './constants';
import messages from './messages';
import type { ScheduledChatFormFieldsProps } from './types';
import { frequencyLabelFor, weekdayLabelFor } from './utils';

const styles = cssMap({
	/** Select menus inside modal-dialog: portal above modal stack (see MenuPortal component override). */
	menuPortal: {
		zIndex: 9999,
	},
	fieldStack: {
		width: '100%',
	},
	scheduleRow: {
		display: 'grid',
		gap: token('space.150'),
		width: '100%',
		alignItems: 'start',
		gridTemplateColumns: 'minmax(0, 1fr)',
	},
	scheduleRowDouble: {
		display: 'grid',
		gap: token('space.150'),
		width: '100%',
		alignItems: 'start',
		gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
	},
	scheduleRowTriple: {
		display: 'grid',
		gap: token('space.150'),
		width: '100%',
		alignItems: 'start',
		gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
	},
	scheduleField: {
		minWidth: '0px',
	},
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const DayMultiOption = (props: OptionProps<any>) => {
	const { children, isSelected } = props;
	return (
		<selectParts.Option {...props}>
			<Checkbox isChecked={isSelected} label={children as string} />
		</selectParts.Option>
	);
};

export const ScheduledChatFormFields = ({
	formatMessage,
	formatDate,
	readonly,
	isActionLoading,
	name,
	prompt,
	frequency,
	daysOfWeek,
	time,
	onNameChange,
	onPromptChange,
	onFrequencyChange,
	onDaysOfWeekChange,
	onTimeChange,
}: ScheduledChatFormFieldsProps): React.JSX.Element => {
	const frequencyOptions = FREQUENCY_VALUES.map((freq) => ({
		value: freq,
		label: frequencyLabelFor(freq, formatMessage),
	}));

	const selectedFrequency = frequencyOptions.find((opt) => opt.value === frequency);

	const dayOptions = SCHEDULED_CHAT_DAYS.map((day) => ({
		value: day,
		label: weekdayLabelFor(day, formatDate),
	}));

	const selectedCustomDays = dayOptions.filter((opt) => daysOfWeek.includes(opt.value));

	const isCustom = frequency === 'custom';
	const isHourly = frequency === 'hourly';
	const showTimeField = !isHourly;
	const showCustomDaysField = isCustom;

	return (
		<Stack space="space.150" xcss={styles.fieldStack}>
			<Stack space="space.050">
				<Label htmlFor="scheduled-chat-name">
					{formatMessage(messages.scheduledChatNameLabel) as string}
					<RequiredAsterisk />
				</Label>
				<Textfield
					id="scheduled-chat-name"
					testId="scheduled-chat-name"
					value={name}
					onChange={(e) => onNameChange(e.currentTarget.value)}
					placeholder={formatMessage(messages.scheduledChatNamePlaceholder)}
					isReadOnly={readonly}
					isDisabled={isActionLoading}
				/>
			</Stack>

			<Stack space="space.050">
				<Label htmlFor="scheduled-chat-prompt">{formatMessage(messages.promptLabel)}</Label>
				<TextArea
					id="scheduled-chat-prompt"
					testId="scheduled-chat-prompt"
					value={prompt}
					onChange={(e) => onPromptChange(e.currentTarget.value)}
					placeholder={formatMessage(messages.promptPlaceholder)}
					isReadOnly={readonly}
					isDisabled={isActionLoading}
					minimumRows={4}
				/>
			</Stack>

			<Box
				xcss={
					isHourly
						? styles.scheduleRow
						: isCustom
							? styles.scheduleRowTriple
							: styles.scheduleRowDouble
				}
			>
				<Stack space="space.050" xcss={styles.scheduleField}>
					<Label htmlFor="scheduled-chat-frequency">{formatMessage(messages.frequencyLabel)}</Label>
					<Select
						inputId="scheduled-chat-frequency"
						testId="scheduled-chat-frequency"
						menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
						components={{
							MenuPortal: (props) => <selectParts.MenuPortal {...props} xcss={styles.menuPortal} />,
						}}
						options={frequencyOptions}
						value={selectedFrequency}
						onChange={(opt) => {
							const next = (opt as OptionType | null)?.value;
							if (typeof next === 'string') {
								const nextFrequency = next as ScheduledChatFrequency;
								onFrequencyChange(nextFrequency);
								if (nextFrequency === 'custom' && daysOfWeek.length === 0) {
									onDaysOfWeekChange([SCHEDULED_CHAT_DAYS[0]]);
								}
							}
						}}
						isDisabled={readonly || isActionLoading}
					/>
				</Stack>

				{showCustomDaysField ? (
					<Stack space="space.050" xcss={styles.scheduleField}>
						<Label htmlFor="scheduled-chat-days">{formatMessage(messages.daysLabel)}</Label>
						<Select
							{...({
								inputId: 'scheduled-chat-days',
								testId: 'scheduled-chat-days',
								menuPortalTarget: typeof document !== 'undefined' ? document.body : undefined,
								isMulti: true,
								isClearable: false,
								closeMenuOnSelect: false,
								hideSelectedOptions: false,
								options: dayOptions,
								value: selectedCustomDays,
								components: {
									Option: DayMultiOption,
									MenuPortal: (props: React.ComponentProps<typeof selectParts.MenuPortal>) => (
										<selectParts.MenuPortal {...props} xcss={styles.menuPortal} />
									),
								},
								// eslint-disable-next-line @typescript-eslint/no-explicit-any
								onChange: (selected: any) => {
									const nextValues = Array.isArray(selected)
										? selected
												// eslint-disable-next-line @typescript-eslint/no-explicit-any
												.map((option: any) => option.value)
												.filter(
													(value): value is ScheduledChatDayOfWeek =>
														typeof value === 'string' &&
														SCHEDULED_CHAT_DAYS.includes(value as ScheduledChatDayOfWeek),
												)
										: [];
									const nextSet = new Set(nextValues);
									onDaysOfWeekChange(SCHEDULED_CHAT_DAYS.filter((day) => nextSet.has(day)));
								},
								isDisabled: readonly || isActionLoading,
								// eslint-disable-next-line @typescript-eslint/no-explicit-any
							} as any)}
						/>
					</Stack>
				) : null}

				{showTimeField ? (
					<Stack space="space.050" xcss={styles.scheduleField}>
						<Label htmlFor="scheduled-chat-time">{formatMessage(messages.timeLabel)}</Label>
						<TimePicker
							id="scheduled-chat-time"
							testId="scheduled-chat-time"
							label=""
							value={time}
							onChange={(next) => onTimeChange(next)}
							isDisabled={readonly || isActionLoading}
							timeIsEditable
							selectProps={{ isClearable: false as const }}
						/>
					</Stack>
				) : null}
			</Box>
		</Stack>
	);
};
